import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const RoadmapPageView = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRoadmapPage();
  }, []);

  const fetchRoadmapPage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/roadmap/page/employee`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (response.ok) {
        const data = await response.json();
        setPage(data);
      }
    } catch (error) {
      console.error('Error fetching roadmap page:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 px-6 py-6 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 py-4 md:px-6 md:py-6 lg:max-w-4xl lg:mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
            <Button onClick={() => navigate('/profile')} variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">2026 Roadmap</h1>
              <p className="text-sm text-gray-500 mt-1">The 2026 roadmap has not been published yet.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <div className="px-0 py-0 md:px-6 md:py-6 lg:max-w-7xl lg:mx-auto">
        <div className="bg-white md:rounded-xl md:shadow-sm md:border md:border-gray-200 p-4 md:p-6 mb-0 md:mb-6">
          <Button onClick={() => navigate('/profile')} variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{page.title}</h1>
          </div>
        </div>

      {/* Sections - Full Width on mobile, constrained on desktop */}
      <div>
        {page.sections?.map((section, sIndex) => (
          <div
            key={section.id || sIndex}
            style={{
              backgroundColor: section.backgroundColor,
              padding: section.padding
            }}
            className="mb-0 md:mb-6 md:rounded-xl md:shadow-sm"
          >
            <div className={`grid gap-6 ${
              section.columnLayout === 1 ? 'grid-cols-1' :
              section.columnLayout === 2 ? 'grid-cols-1 md:grid-cols-2' :
              section.columnLayout === 3 ? 'grid-cols-1 md:grid-cols-3' :
              'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
            }`}>
              {section.columns?.map((column, cIndex) => (
                <div key={column.id || cIndex}>
                  {column.blocks?.map((block, bIndex) => {
                    if (block.type === 'image') {
                      // Skip rendering if image URL is empty
                      if (!block.url || block.url.trim() === '') {
                        return null;
                      }
                      
                      return (
                        <div key={bIndex} className="mb-6">
                          {block.link ? (
                            <a href={block.link} target="_blank" rel="noopener noreferrer">
                              <img 
                                src={block.url} 
                                alt={block.alt || ''} 
                                style={{ width: block.width }} 
                                className="rounded-lg"
                              />
                            </a>
                          ) : (
                            <img 
                              src={block.url} 
                              alt={block.alt || ''} 
                              style={{ width: block.width }} 
                              className="rounded-lg"
                            />
                          )}
                        </div>
                      );
                    } else if (block.type === 'heading') {
                      const HeadingTag = block.level;
                      return (
                        <HeadingTag 
                          key={bIndex} 
                          style={{ color: block.color }} 
                          className={`mb-4 font-bold ${
                            block.level === 'h1' ? 'text-4xl' :
                            block.level === 'h2' ? 'text-3xl' :
                            'text-2xl'
                          }`}
                        >
                          {block.text}
                        </HeadingTag>
                      );
                    } else if (block.type === 'paragraph') {
                      return (
                        <div 
                          key={bIndex} 
                          className="mb-6 prose max-w-none text-gray-700" 
                          dangerouslySetInnerHTML={{ __html: block.content }} 
                        />
                      );
                    } else if (block.type === 'button') {
                      return (
                        <div key={bIndex} className={`mb-6 text-${block.alignment}`}>
                          <a
                            href={block.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              backgroundColor: block.backgroundColor,
                              color: block.textColor
                            }}
                            className="inline-block px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                          >
                            {block.text}
                          </a>
                        </div>
                      );
                    } else if (block.type === 'imageWithText') {
                      const ImageContent = block.link ? (
                        <a href={block.link} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={block.url} 
                            alt={block.alt || ''} 
                            className="rounded-lg w-full h-auto"
                          />
                        </a>
                      ) : (
                        <img 
                          src={block.url} 
                          alt={block.alt || ''} 
                          className="rounded-lg w-full h-auto"
                        />
                      );

                      return (
                        <div key={bIndex} className={`mb-6 grid gap-6 ${block.imagePosition === 'right' ? 'md:grid-cols-2' : 'md:grid-cols-2'}`}>
                          {block.imagePosition === 'left' ? (
                            <>
                              <div>{ImageContent}</div>
                              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: block.content }} />
                            </>
                          ) : (
                            <>
                              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: block.content }} />
                              <div>{ImageContent}</div>
                            </>
                          )}
                        </div>
                      );
                    } else if (block.type === 'accordion') {
                      return (
                        <details key={bIndex} className="mb-4 rounded-lg overflow-hidden border border-gray-200">
                          <summary
                            style={{
                              backgroundColor: block.titleBgColor || '#00b48f',
                              color: block.titleColor || '#ffffff'
                            }}
                            className="px-6 py-4 font-semibold cursor-pointer hover:opacity-90 transition-opacity"
                          >
                            {block.title}
                          </summary>
                          <div
                            style={{
                              backgroundColor: block.contentBgColor || '#f3f4f6',
                              color: block.contentColor || '#374151'
                            }}
                            className="px-6 py-4 prose max-w-none"
                            dangerouslySetInnerHTML={{ __html: block.content }}
                          />
                        </details>
                      );
                    }
                    return null;
                  })}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
};

export default RoadmapPageView;
